import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import RequirementsProgress from "../components/RequirementsProgress";
import { toast } from "sonner";
import { Inbox, Layers, CheckCircle2, Loader2, Clock, Search, ExternalLink, Lock } from "lucide-react";

const cityOf = (addr) => {
  const m = (addr || "").split(",");
  return m.length >= 2 ? m[m.length - 2].trim() : "";
};

const StatusPill = ({ status }) => {
  const map = {
    "Open Order": "bg-amber-100 text-amber-800 border-amber-300",
    "In Progress": "bg-blue-100 text-blue-800 border-blue-300",
    "Completed": "bg-emerald-100 text-emerald-800 border-emerald-300",
    "Submitted": "bg-teal-100 text-teal-800 border-teal-300",
    "Cancelled": "bg-rose-100 text-rose-800 border-rose-300",
    "Pending for Documents": "bg-cyan-100 text-cyan-800 border-cyan-300",
    "Tax Pending": "bg-orange-100 text-orange-800 border-orange-300",
    "Need to Call for Taxes": "bg-red-100 text-red-800 border-red-300",
    "Typing Pending": "bg-indigo-100 text-indigo-800 border-indigo-300",
    "ABS Order Quality/Final Review": "bg-purple-100 text-purple-800 border-purple-300",
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${map[status] || map["Open Order"]}`}>{status}</span>;
};

const TERMINAL = ["Completed", "Cancelled", "Submitted"];

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState({});
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [gated, setGated] = useState(false);
  const [clockingIn, setClockingIn] = useState(false);

  useEffect(() => {
    let stop = false;
    const poll = async () => { try { const { data } = await api.get("/chat/unread"); if (!stop) setUnread(data.orders || {}); } catch (e) { console.error("Unread poll failed:", e); } };
    poll();
    const t = setInterval(poll, 5000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [mine, avail] = await Promise.all([api.get(`/orders?scope=queue`), api.get(`/queue/available`)]);
      setOrders(mine.data); setAvailable(avail.data); setGated(false);
    }
    catch (e) {
      if (e?.response?.status === 403 && e?.response?.data?.detail === "clock_in_required") setGated(true);
      else toast.error("Failed to load orders");
    }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("timeclock-changed", onChange);
    return () => window.removeEventListener("timeclock-changed", onChange);
  }, []);

  const clockInFromGate = async () => {
    setClockingIn(true);
    try {
      await api.post("/timeclock/clock-in");
      toast.success("Clocked in — welcome, your queue is ready");
      window.dispatchEvent(new Event("timeclock-changed"));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clock in"); }
    finally { setClockingIn(false); }
  };

  const claim = async (orderId) => {
    try { await api.post(`/orders/${orderId}/claim`); toast.success("Order claimed — added to your queue"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not claim order"); }
  };

  const active = orders.filter((o) => !TERMINAL.includes(o.status));
  const inProgress = active.filter((o) => o.status !== "Open Order");
  let shown = tab === "active" ? active : available;
  if (q.trim()) {
    const t = q.toLowerCase();
    shown = shown.filter((o) => [o.order_ref, o.borrower, o.property_address, o.county, o.state].join(" ").toLowerCase().includes(t));
  }

  const TABS = [
    { key: "active", label: "Accepted Orders", n: active.length },
    { key: "available", label: "Available to Claim", n: available.length },
  ];

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-slate-100 -m-6 p-6 text-slate-800">
      {gated ? (
        <div data-testid="clock-in-gate" className="flex min-h-[60vh] items-center justify-center">
          <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#1A395E]/10">
              <Lock className="h-7 w-7 text-[#1A395E]" />
            </div>
            <h2 className="text-xl font-700 text-[#1A395E]">Clock in to start your shift</h2>
            <p className="mt-2 text-sm text-slate-500">
              Your assigned orders are locked until you log in on the Time Clock. Clock in to view and work your queue.
            </p>
            <button data-testid="gate-clock-in-btn" onClick={clockInFromGate} disabled={clockingIn}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-teal px-5 py-2.5 text-sm font-semibold text-teal-foreground hover:bg-teal/90 transition-colors disabled:opacity-60">
              {clockingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />} Log In (Clock In)
            </button>
          </div>
        </div>
      ) : (
      <>
      <div className="mb-4">
        <h1 className="font-display text-2xl font-700 tracking-tight text-[#1A395E]">My Queue</h1>
        <p className="text-sm text-slate-500 mt-0.5">Your assigned live orders — open one to view full details and work the search.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div data-testid="kpi-active" className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between shadow-sm">
          <div><div className="text-[11px] uppercase font-semibold text-slate-400">Active</div><div className="text-2xl font-700 text-[#1A395E]">{active.length}</div></div><Layers className="h-5 w-5 text-blue-500" />
        </div>
        <div data-testid="kpi-inprogress" className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between shadow-sm">
          <div><div className="text-[11px] uppercase font-semibold text-slate-400">In Progress</div><div className="text-2xl font-700 text-blue-700">{inProgress.length}</div></div><Clock className="h-5 w-5 text-blue-500" />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3 shadow-sm flex items-center gap-3">
        <span className="text-xs font-bold uppercase tracking-wide text-[#1A395E]">Search</span>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order #, borrower, property…"
            className="w-72 rounded-md border border-slate-300 bg-white pl-8 pr-3 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-[#1A395E] focus:outline-none" />
        </div>
        <span className="ml-auto text-xs text-slate-500">{shown.length} shown</span>
      </div>

      <div className="flex items-end gap-1">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`rounded-t-md border border-b-0 px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-[#1A395E] text-white border-[#1A395E]" : "bg-blue-50 text-[#1A395E] border-slate-200 hover:bg-blue-100"}`}>
            {t.label} — {t.n}
          </button>
        ))}
      </div>

      <div className="rounded-b-lg rounded-tr-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full table-fixed text-left text-[13px]">
          <colgroup>
            <col className="w-[11%]" /><col className="w-[8%]" /><col className="w-[15%]" /><col className="w-[15%]" />
            <col className="w-[17%]" /><col className="w-[6%]" /><col className="w-[11%]" /><col className="w-[10%]" /><col className="w-[7%]" />
          </colgroup>
          <thead className="bg-[#1A395E] text-white">
            <tr className="text-[11px] uppercase tracking-wide">
              <th className="px-2 py-2 font-semibold">Order #</th>
              <th className="px-2 py-2 font-semibold">Date</th>
              <th className="px-2 py-2 font-semibold">Product</th>
              <th className="px-2 py-2 font-semibold">Borrower</th>
              <th className="px-2 py-2 font-semibold">Property</th>
              <th className="px-2 py-2 font-semibold">State</th>
              <th className="px-2 py-2 font-semibold">County</th>
              <th className="px-2 py-2 font-semibold">Status</th>
              <th className="px-2 py-2 font-semibold text-center">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-400">Loading…</td></tr>}
            {!loading && shown.map((o, idx) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} className={`${idx % 2 ? "bg-slate-50" : "bg-white"} hover:bg-blue-50/60 transition-colors`}>
                <td className="px-2 py-2 align-top">
                  <span className="inline-flex items-center gap-1.5">
                    <button data-testid={`order-link-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)}
                      className="font-mono text-[#1A6FBF] font-semibold hover:underline truncate max-w-full text-left">{o.order_ref}</button>
                    {unread[o.id] > 0 && <span data-testid={`row-unread-${o.id}`} className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold h-4 min-w-[16px] px-1">{unread[o.id]}</span>}
                  </span>
                  {o.needs_review && <div className="mt-0.5 inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold text-red-700">Review</div>}
                  {o.digital_search?.status === "running" && <div data-testid={`portals-badge-${o.id}`} title="Auto digital search running on Duval county portals" className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700"><Loader2 className="h-2.5 w-2.5 animate-spin" /> Searching…</div>}
                  {o.digital_search?.status === "done" && o.digital_search?.captured > 0 && <div data-testid={`portals-badge-${o.id}`} title={`Auto-searched Duval portals — ${o.digital_search.captured} capture(s) attached`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700"><CheckCircle2 className="h-2.5 w-2.5" /> Portals ✓ {o.digital_search.captured}</div>}
                </td>
                <td className="px-2 py-2 align-top text-slate-500 whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"}</td>
                <td className="px-2 py-2 align-top text-slate-700 truncate" title={o.product_type}>{o.product_type}</td>
                <td className="px-2 py-2 align-top text-slate-700 truncate" title={o.borrower}>{o.borrower || "—"}</td>
                <td className="px-2 py-2 align-top text-slate-600 truncate" title={o.property_address}>{cityOf(o.property_address) || o.property_address || "—"}</td>
                <td className="px-2 py-2 align-top text-slate-700 font-semibold">{o.state || "—"}</td>
                <td className="px-2 py-2 align-top text-slate-600 truncate" title={o.county}>{o.county || "—"}</td>
                <td className="px-2 py-2 align-top">
                  <StatusPill status={o.status} />
                  <div className="mt-1"><RequirementsProgress order={o} testid={`req-progress-${o.id}`} /></div>
                  {(o.search_links || []).length > 0 && (
                    <a href={o.search_links[0].url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#1A6FBF] hover:underline"><ExternalLink className="h-2.5 w-2.5" /> County search</a>
                  )}
                </td>
                <td className="px-2 py-2 align-top text-center">
                  {tab === "available" ? (
                    <button data-testid={`claim-${o.id}`} onClick={() => claim(o.id)}
                      className="rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 transition-colors">Claim</button>
                  ) : (
                    <button data-testid={`open-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)}
                      className="rounded-md bg-[#1A395E] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#24497A] transition-colors">Open</button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && !shown.length && (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-sm text-slate-400"><Inbox className="h-7 w-7 mx-auto text-slate-300 mb-2" />No orders in this tab.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}
